import type { ReactNode } from "react";
import DataTable from "react-data-table-component";
import { ChevronRight, Loader2 } from "lucide-react";
import type { Contract, ContractListPagination } from "../../types/contract";
import { formatContractStatusForDisplay } from "../../utils/contractStatus";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import { AddinDataTablePagination } from "../molecules/AddinDataTablePagination";

type ContractListTableProps = {
  contracts: Contract[];
  isLoading: boolean;
  pagination: ContractListPagination;
  openingContractId: string | null;
  onContractTitleClick: (contract: Contract) => void;
  onGoToReview: (contract: Contract) => void;
  onPageChange: (page: number) => void;
};

const isReviewChevronDisabled = (row: Contract, openingContractId: string | null): boolean => {
  if (openingContractId === row.id) {
    return true;
  }
  if (row.status === "Completed") {
    return true;
  }
  return (
    row.status === "processing" ||
    row.status === "processing_failed" ||
    row.status === "ai_in_progress"
  );
};

const originBadge = (row: Contract): ReactNode => {
  if (row.sourceKind === "template_generated") {
    return (
      <span className="mr-1.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
        Template
      </span>
    );
  }
  if (row.sourceKind === "upload_review") {
    return (
      <span className="mr-1.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
        Upload
      </span>
    );
  }
  return null;
};

const statusBadgeClass = (status: Contract["status"]) => {
  switch (status) {
    case "Approved":
      return "bg-green-100 text-green-800";
    case "Completed":
      return "bg-green-100 text-green-800";
    // case "Under Review":
    //   return "bg-amber-100 text-amber-800";
    case "Pending Review":
    case "in_review":
    case "Assign to Reviewer":
      return "bg-amber-100 text-amber-800";
    case "ai_in_progress":
    case "processing":
      return "bg-blue-100 text-blue-800";
    case "processing_failed":
      return "bg-red-100 text-red-800";
    case "Draft":
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const ContractListTable = ({
  contracts,
  isLoading,
  pagination,
  openingContractId,
  onContractTitleClick,
  onGoToReview,
  onPageChange,
}: ContractListTableProps) => {
  const columns = [
    {
      name: "Contract",
      grow: 1,
      cell: (row: Contract) => {
        const chevronDisabled = isReviewChevronDisabled(row, openingContractId);
        return (
          <div className="relative flex min-w-0 flex-1 items-start gap-1 py-3 pr-2">
            <div className="min-w-0 flex-1 pr-8">
              <button
                type="button"
                disabled={openingContractId === row.id}
                className="w-full text-left font-medium text-foreground transition-all duration-200 ease-in-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait disabled:opacity-60"
                onClick={(e) => {
                  e.stopPropagation();
                  onContractTitleClick(row);
                }}
              >
                {row.title}
              </button>
              <p className="mt-0.5 text-xs text-muted-foreground flex flex-wrap items-center gap-y-1">
                {originBadge(row)}
                <span>
                  {row.type} • {row.date}
                </span>
              </p>
            </div>
            <div className="z-20 mt-0.5 shrink-0 self-start">
              <button
                type="button"
                disabled={chevronDisabled}
                title={
                  chevronDisabled && row.status === "Completed"
                    ? "Open the document from the title; review details are not available for completed contracts"
                    : chevronDisabled
                      ? "Not available while this contract is processing"
                      : "Open review details"
                }
                aria-label={
                  chevronDisabled
                    ? row.status === "Completed"
                      ? "Review details disabled for completed contract"
                      : "Review details unavailable"
                    : "Open review details"
                }
                className={`rounded p-1 transition-all duration-200 ease-in-out ${
                  chevronDisabled
                    ? "cursor-not-allowed text-muted-foreground/40"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!chevronDisabled) {
                    onGoToReview(row);
                  }
                }}
              >
                <ChevronRight className="h-4 w-4" aria-hidden strokeWidth={2} />
              </button>
            </div>
            <span
              className={`absolute -right-1 -top-3.5 z-10 border border-inherit px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
            >
              {formatContractStatusForDisplay(row.status)}
            </span>
          </div>
        );
      },
    },
  ];

  const contractListTableStyles = {
    ...dataTableCustomStyles,
    table: {
      ...dataTableCustomStyles.table,
      style: {
        ...dataTableCustomStyles.table?.style,
        borderRadius: "none",
      },
    },
  };

  const tableLoadingComponent = (
    <div className="flex flex-col items-center justify-center gap-2 py-8" role="status" aria-live="polite">
      <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
      <span className="text-sm text-muted-foreground">Loading contracts…</span>
    </div>
  );

  return (
    <DataTable
      columns={columns}
      data={contracts}
      customStyles={contractListTableStyles}
      noTableHead
      progressPending={isLoading}
      progressComponent={tableLoadingComponent}
      pagination
      paginationServer
      paginationTotalRows={pagination.totalItems}
      paginationPerPage={pagination.pageSize}
      paginationDefaultPage={pagination.page}
      paginationRowsPerPageOptions={[pagination.pageSize]}
      paginationComponent={AddinDataTablePagination}
      onChangePage={onPageChange}
    />
  );
};
